import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FeatureToggleEnum, TARIFF_CHANGE_EMOBILITY_ROUTE, TARIFF_CHANGE_ROUTE } from '@mwe/constants';
import { Router } from '@angular/router';
import { createAddress, IPopupModelV2, ModalChild, Product, Sparte } from '@mwe/models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AbstractTariffChangeLogic, ProfileService } from '@mwe/services';
import { TranslatePipe } from '@ngx-translate/core';
import { BadgeComponent } from '../badge/badge/badge.component';
import { ClickableProductsCardComponent } from '../cards/clickable-products-card/clickable-products-card.component';
import { ModalComponent } from '../modal/modal.component';
import {
  containsEmobility,
  containsFernwaerme,
  containsStromOrGasCategory,
  getCompanyCorrelationFromCategory,
  isProductCategoryEmobility,
  isProductCategoryFernwaerme,
  isProductCategoryGas,
  isProductCategoryStrom,
  sortProductsEnumCategory,
} from '@mwe/utils';

@Component({
  selector: 'mwe-tariff-change-selection-popup',
  templateUrl: './tariff-change-selection-popup.component.html',
  imports: [BadgeComponent, ClickableProductsCardComponent, ModalComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffChangeSelectionPopupComponent implements ModalChild {
  private readonly router = inject(Router);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly profileService = inject(ProfileService);
  private readonly tariffChangeLogic = inject(AbstractTariffChangeLogic);

  loadedData: { availableCategories: string[]; products: Product[] }; // data passed by popupService

  readonly popupModel = signal<IPopupModelV2>(null);

  readonly stromCompany = getCompanyCorrelationFromCategory(Sparte.Strom);
  readonly emobilityCompany = getCompanyCorrelationFromCategory(Sparte.Emobility);
  readonly waermeCompany = getCompanyCorrelationFromCategory(Sparte.Fernwaerme);

  readonly isEmobilityDisabled = computed<boolean>(
    () => !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.CHARGING_CARD_TARIFF_CHANGE_ENABLED),
  );

  private readonly categories = computed(() => {
    return [...new Set(this.popupModel().componentData.availableCategories as Sparte[])];
  });

  private readonly stromCategories = computed(() => this.categories().filter(isProductCategoryStrom));
  private readonly gasCategories = computed(() => this.categories().filter(isProductCategoryGas));
  private readonly waermeCategories = computed(() => this.categories().filter(isProductCategoryFernwaerme));

  readonly hasStromGas = computed<boolean>(() => containsStromOrGasCategory(this.categories()));
  readonly hasWaerme = computed(() => containsFernwaerme(this.categories()));
  readonly hasEmobility = computed<boolean>(() => containsEmobility(this.categories()));

  readonly stromGasWaermeCategories = computed<Sparte[]>(
    () => sortProductsEnumCategory([...this.stromCategories(), ...this.gasCategories(), ...this.waermeCategories()], true) as Sparte[],
  );

  readonly emobilityCategories = computed(() => this.categories().filter(isProductCategoryEmobility));

  startStromGasTariffChange(): void {
    this.closePopup();
    if (this.loadedData.products) {
      const address = createAddress(this.loadedData.products[0].address);
      this.tariffChangeLogic.initProcess(address, this.loadedData.products);
      return;
    }

    this.router.navigateByUrl(`/${TARIFF_CHANGE_ROUTE}`);
  }

  startEmobilityTariffChange(): void {
    this.closePopup();

    // FT active -> new emobility tariff change route, FT inactive -> old S/G tariff change wizard
    const route = this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.CHARGING_CARD_TARIFF_CHANGE_ENABLED)
      ? TARIFF_CHANGE_EMOBILITY_ROUTE
      : TARIFF_CHANGE_ROUTE;
    this.router.navigateByUrl(`/${route}`);
  }

  closePopup(): void {
    this.activeModal.close();
  }
}
